/**
 * EAA-1815: curated primary-color swatches for the branding editor (frame
 * "26 branding-editor.png").
 *
 * The pre-existing editor let an admin type ANY hex into a free text field.
 * The design frame explicitly rules that out — `org.branding.primary_color`
 * is used as a solid button fill with WHITE text on the Keycloak login theme
 * (`apps/sso/src/login/branding.ts` → `--emg-brand-primary`), so a pale color
 * (e.g. a light yellow) makes that button's label unreadable. Every entry
 * below is fixed at build time and pre-checked at >= 4.5:1 contrast against
 * white (WCAG AA, normal text) — there is no free picker, only this list.
 *
 * `hoverHex` is a hand-picked darker shade of the same hue (not a computed
 * darken) so hover states stay visually intentional rather than a formula
 * artifact; darker always improves contrast, so hover shades are never
 * rechecked individually.
 */

export interface BrandSwatch {
    /** Stable key for React lists and equality checks — never the hex itself
     * (a legacy org can have a hex that collides with a relabeled swatch). */
    key: string;
    /** Vietnamese color name shown as the swatch's accessible label/tooltip. */
    label: string;
    hex: string;
    hoverHex: string;
}

export const BRAND_SWATCHES: BrandSwatch[] = [
    { key: 'emg-teal', label: 'Xanh lam EMG (mặc định)', hex: '#007FA3', hoverHex: '#006888' },
    { key: 'purple', label: 'Tím', hex: '#7C3AED', hoverHex: '#6D28D9' },
    { key: 'teal-green', label: 'Xanh ngọc', hex: '#0F766E', hoverHex: '#115E59' },
    { key: 'green', label: 'Xanh lá', hex: '#047857', hoverHex: '#065F46' },
    { key: 'rust', label: 'Cam đất', hex: '#C2410C', hoverHex: '#9A3412' },
    { key: 'rose', label: 'Đỏ mận', hex: '#9F1239', hoverHex: '#881337' },
    { key: 'royal-blue', label: 'Xanh dương', hex: '#1D4ED8', hoverHex: '#1E40AF' },
    { key: 'indigo', label: 'Chàm', hex: '#4338CA', hoverHex: '#3730A3' },
    { key: 'fuchsia', label: 'Hồng cánh sen', hex: '#86198F', hoverHex: '#701A75' },
    { key: 'slate', label: 'Xám than', hex: '#334155', hoverHex: '#1E293B' },
];

export const EMG_DEFAULT_SWATCH = BRAND_SWATCHES[0];

function srgbToLinear(channel: number): number {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number | null {
    const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!match) return null;
    const value = match[1];
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/**
 * WCAG contrast ratio of white text (#fff) on top of `hex`. Returns null for
 * an unparseable value (only reachable via a legacy free-typed hex from
 * before this ticket — the curated swatches always parse).
 */
export function contrastRatioOnWhite(hex: string): number | null {
    const luminance = relativeLuminance(hex);
    if (luminance === null) return null;
    return (1 + 0.05) / (luminance + 0.05);
}

/** Vietnamese decimal comma, one place — matches frame 26's "5,7:1". */
export function formatContrastRatioVi(ratio: number): string {
    return `${ratio.toFixed(1).replace('.', ',')}:1`;
}

export function findSwatchByHex(hex: string | undefined): BrandSwatch | undefined {
    if (!hex) return undefined;
    const normalized = hex.trim().toLowerCase();
    return BRAND_SWATCHES.find((swatch) => swatch.hex.toLowerCase() === normalized);
}
